import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Guid } from 'guid-typescript';
import { Project, ProjectBudget, ProjectBudgetItem } from '../../../../../../common/models/project';
import { FileInfo } from '../../../../../../common/models/file';
import { CostType, ProjectBudgetType } from '../../../../../../common/models/common';
import { ProjectService } from '../../../../../../common/services/project.service';
import { FileService } from '../../../../../../common/services/file.service';
import { Subject, takeUntil } from 'rxjs';


@Component({
  standalone: true,
  selector: 'app-budget-view',
  templateUrl: './budget.component.html',
  styleUrls: ['./budget.component.scss'],
  imports: [
    CommonModule,
    MatStepperModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule
],
})
export class ProjectBudgetComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  isHandset: boolean = false;

  projectId!: string;
  projectBudgetId!: string;

  budget: ProjectBudget | null = null;

  budgetItemFileMap: { [itemId: string]: FileInfo[] } = {};


  budgetInfoFields = [
    { label: 'Inačica proračuna', property: 'budgetVersion', type: 'number' },
    { label: 'Vrsta proračuna', property: 'budgetType', type: 'enum' }
  ];

  budgetInfoFieldValues: Array<{ label: string; value: any; type: string }> = [];

  get directBudgetItems(): ProjectBudgetItem[] {
    return this.budget?.projectBudgetItems?.filter(item => item.type === CostType.Direct) ?? [];
  }

  get indirectBudgetItems(): ProjectBudgetItem[] {
    return this.budget?.projectBudgetItems?.filter(item => item.type === CostType.Indirect) ?? [];
  }

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private fileService: FileService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$)).subscribe((result) => {
      this.isHandset = result.matches;
    });

    const projectIdParam = this.route.snapshot.paramMap.get('projectId');
    const projectBudgetIdParam = this.route.snapshot.paramMap.get('projectBudgetId');

    if (!projectIdParam || !projectBudgetIdParam) {
      console.error('Missing projectId or budgetId in route');
      return;
    }

    this.projectId = projectIdParam;
    this.projectBudgetId = projectBudgetIdParam;

    this.projectService.getProjectBudget(Guid.parse(this.projectId), Guid.parse(this.projectBudgetId)).subscribe({
      next: (projectBudget) => {


        this.budget = ProjectBudget.fromJson(projectBudget);

        this.processBudgetInfoFields();
        this.loadBudgetItemAttachments();
      },
      error: (err) => console.error('Error fetching project budget', err),
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private processBudgetInfoFields(): void {
    if (!this.budget) {
      return;
    }

    this.budgetInfoFieldValues = this.budgetInfoFields.map((field) => ({
      label: field.label,
      value: (this.budget as any)[field.property],
      type: field.type,
    }));
  }


  private loadBudgetItemAttachments(): void {
    if (!this.budget) {
      return;
    }

    for (const item of this.budget.projectBudgetItems) {
      if (!item.fileStorageLocation) {
        continue;
      }
      this.fileService.listDocuments(item.fileStorageLocation).pipe(takeUntil(this.destroy$)).subscribe({
        next: (files) => {
          this.budgetItemFileMap[item.id] = files;
        },
        error: (err) => {
          console.error('Error loading attachments for project budget item:', item.id, err);
        },
      });
    }
  }


  downloadFile(file: FileInfo) {
    this.fileService.downloadDocument(file.link).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading file', err);
      },
    });
  }

  getBudgetTypeLabel(type: ProjectBudgetType): string {
    switch (type) {
      case ProjectBudgetType.InitialCopy:
        return 'Inicijalni proračun';
      case ProjectBudgetType.Reallocation:
        return 'Prenamjena sredstava';
      default:
        return 'Nepoznata vrsta proračuna';
    }
  }

  getFundingSourceTypeLabel(type: number): string {
    switch (type) {
      case 1:
        return 'I. Vlastiti izvori';
      case 2:
        return 'II. Ostala tijela javne vlasti';
      case 3:
        return 'III. Jedinice lokalne i područne (regionalne) samouprave';
      case 4:
        return 'IV. Drugo';
      default:
        return 'Nepoznat izvor';
    }
  }
}
